import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import type {DeferredImageUploadData, TempImageData} from '@Libraries/add-media-library';

/**
 * What the upload flow hands back per uploaded file: a grid storage for fully
 * processed media, or a TempImageData/DeferredImageUploadData that bypasses the grid.
 */
export type UploadedMediaItem = GridItemStorage | TempImageData | DeferredImageUploadData;

export const USER_IMAGE_UPLOADS_GRID_ID = 'USER_IMAGE_UPLOADS_GRID';
export const USER_IMAGE_UPLOADED_ONLY_GRID_ID = 'USER_IMAGE_UPLOADED_ONLY_GRID';
export const USER_VIDEO_UPLOADED_ONLY_GRID_ID = 'USER_VIDEO_UPLOADED_ONLY_GRID';
export const USER_AUDIO_UPLOADED_ONLY_GRID_ID = 'USER_AUDIO_UPLOADED_ONLY_GRID';
export const USER_AI_GENERATED_IMAGE_GRID_ID = 'USER_AI_GENERATED_IMAGE_GRID';
export const USER_AI_GENERATED_AUDIO_GRID_ID = 'USER_AI_GENERATED_AUDIO_GRID';
export const USER_AI_GENERATED_VIDEO_GRID_ID = 'USER_AI_GENERATED_VIDEO_GRID';
export const USER_VIDEO_UPLOADS_GRID_ID = 'USER_VIDEO_UPLOADS_GRID';
export const USER_AUDIO_UPLOADS_GRID_ID = 'USER_AUDIO_UPLOADS_GRID';
export const USER_LOGOS_GRID_ID = 'USER_LOGOS_GRID';
export const USER_UPLOADS_SELECTION_GRID_ID = 'USER_UPLOADS_SELECTION_GRID';
export const COLLECTION_GRID_ID = 'UPLOADS_COLLECTION_GRID';
export const NEW_COLLECTION_BUTTON_GRID_ITEM_ID = 'newCollectionButton';
export const COLLECTION_MEDIA_GRID_ID = 'MEDIA_UPLOADS_COLLECTION_GRID';
export const FACEBOOK_PHOTOS_GRID = 'FACEBOOK_PHOTOS_GRID';
export const USER_UPLOADS_MEDIA_TABS = 'USER_UPLOADS_MEDIA_TABS';
export const USER_UPLOADS_MEDIA_SUB_TABS = 'USER_UPLOADS_MEDIA_SUB_TABS';
export const COOKIE_FB_SESSION_KEY = 'pmwfbsk';
export const UPLOADS_GRID_ITEM_HEIGHT = 130;

export enum UploadsMediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  IMAGE_BACKGROUND = 'IMAGE_BACKGROUND',
  VIDEO_BACKGROUND = 'VIDEO_BACKGROUND',
}

export enum UploadsMediaSubTab {
  ALL = 'ALL',
  UPLOADS = 'UPLOADS',
  LOGO = 'LOGO',
  AI_IMAGE = 'AI_IMAGE',
  AI_VOICE = 'AI_VOICE',
  AI_VIDEO = 'AI_VIDEO',
}

/**
 * Values are the wire format for the `sortBy` param of the collection and AI-creations listing
 * endpoints and must match the backend UserMediaSortOrder enum. Folders support every order on every tab (recency =
 * folder modified or an item from it added to a design, via last_modified_on). Items support
 * recency (last_used_on, stamped on add-to-design) and newest/oldest everywhere; alphabetical
 * sorts audio by name while image/video clamp to newestFirst (no name column).
 */
export enum UploadsSortOrder {
  RECENTLY_USED = 'recentlyUsed',
  NEWEST_FIRST = 'newestFirst',
  OLDEST_FIRST = 'oldestFirst',
  ALPHABETICAL_A_TO_Z = 'alphabeticalAToZ',
  ALPHABETICAL_Z_TO_A = 'alphabeticalZToA',
}

/**
 * Values are the wire format for the `sources` param of collection/getUserMultiMediaItemsInCollection
 * and must match the backend UserMediaSourceGroup enum.
 */
export enum UploadsSourceGroup {
  ALL = 'all',
  UPLOADED_ONLY = 'uploadedOnly',
  BRAND_LOGOS_ONLY = 'brandLogosOnly',
}

/**
 * The endpoints that list the items grid: each AI Creations pill has its own, and every other pill (All, Uploaded,
 * Logos, an open folder) lists through the collection endpoint narrowed by `sources`.
 */
export enum UploadsItemsEndpoint {
  AI_IMAGES = 'stockmedia/getGeneratedImagesInUploadsByUser',
  AI_VIDEOS = 'aivideo/getGeneratedVideosInUploadsByUser',
  AI_VOICES = 'aivoice/getGeneratedAudiosInUploadsByUser',
  COLLECTION_ITEMS = 'collection/getUserMultiMediaItemsInCollection',
}

/** The primary tab and sub-pill the uploads panel shows. */
export interface UploadsTabState {
  tab: UploadsMediaType;
  subTab: UploadsMediaSubTab;
}

/** What the items grid lists: the tab and sub-pill, the open folder ('' at the root) and the order to send (getUploadsItemsSortOrder). */
export interface UploadsListing extends UploadsTabState {
  collectionId: string;
  sortOrder: UploadsSortOrder;
}

/** One page of a listing that takes the short paging params. */
export interface UploadsItemsPageParams {
  o: number;
  l: number;
  sortBy: UploadsSortOrder;
}

/** One page of AI videos; this endpoint spells its paging params out. */
export interface AiVideosPageParams {
  offset: number;
  limit: number;
  sortBy: UploadsSortOrder;
}

/** One page of the root's or a folder's items, narrowed to the sub-pill's sources. */
export interface CollectionItemsPageParams extends UploadsItemsPageParams {
  c: string;
  collectionType: UploadsMediaType;
  sources: UploadsSourceGroup;
}

/** The call that fetches one page of the items grid. */
export interface UploadsItemsRequest {
  endpoint: UploadsItemsEndpoint;
  params: UploadsItemsPageParams | AiVideosPageParams | CollectionItemsPageParams;
}

/** Inputs for the order the items grid follows; see getUploadsItemsSortOrder. */
export interface UploadsItemsSortOrderParams {
  sortOrder: UploadsSortOrder;
  lastNonAlphabeticalSortOrder: UploadsSortOrder;
  activeTab: UploadsMediaType;
}

/** Inputs for which sort orders the sort dropdown offers; see getVisibleUploadsSortOrders. */
export interface UploadsSortVisibilityParams {
  activeTab: UploadsMediaType;
  activeSubtab: UploadsMediaSubTab;
  doesUserHaveFolders: boolean;
}

/** Inputs for the order the sort dropdown shows as chosen; see getDisplayedUploadsSortOrder. */
export type DisplayedUploadsSortOrderParams = UploadsItemsSortOrderParams & UploadsSortVisibilityParams;

/** A folder to insert into the collections grid; an empty collectionId means it is not saved yet. */
export interface CollectionToAddToGrid {
  collectionId: string;
  collectionName: string;
}

/** Where a folder added to the collections grid lands. */
export enum NewFolderGridPlacement {
  /** Right after the leading tiles, whatever the sort order: an unsaved tile stays in view while it is named. */
  LEADING = 'leading',
  /** Where the active sort order lists it (see getNewFolderGridIndex). */
  SORTED = 'sorted',
}

/** A folder tile that just got saved and now takes the position the sort order gives its name. */
export interface SavedFolderToPlace {
  folderId: string;
  folderName: string;
}

export interface SavedFolderGridIndexParams extends SavedFolderToPlace {
  folderGridItems: GridItemStorage[] | undefined;
  sortOrder: UploadsSortOrder;
}

export interface NewFolderGridIndexParams {
  folderGridItems: GridItemStorage[] | undefined;
  sortOrder: UploadsSortOrder;
  newFolderName: string;
  /** How many tiles the layout lists ahead of every folder (the large-screen create-folder tile). */
  leadingGridItemsCount: number;
}

export interface AlbumData {
  aid: string;
  cover: string;
  name: string;
  size: string;
}

export interface FacebookResponse {
  albums: AlbumData[];
}

export interface FacebookPhotosResponse {
  id: string;
  s: string;
  b: string;
  width: number;
  height: number;
}

export enum UserMediaPanelType {
  DEFAULT = 'DEFAULT',
  RECORD_VIDEO = 'RECORD_VIDEO',
  RECORD_AUDIO = 'RECORD_AUDIO',
  RECORD_PHOTO = 'RECORD_PHOTO',
  AI_IMAGE = 'AI_IMAGE',
  AI_VOICE = 'AI_VOICE',
  AI_VIDEO = 'AI_VIDEO',
}

export enum UserMediaPanelAiOption {
  AI_IMAGE = 'AI_IMAGE',
  AI_VOICE = 'AI_VOICE',
  AI_VIDEO = 'AI_VIDEO',
}
